/**
 * Global "is the API reachable?" monitor.
 *
 * Why this exists: the SPA and the API are two separate IIS sites, deployed
 * separately. During a release the static SPA keeps being served while the API's
 * app pool is stopped (msdeploy's AppOffline rule) and then restarts to run
 * migrations. For that window every request the open page makes fails, and until
 * now each view swallowed it into its own catch — so the operator saw empty grids,
 * spinners that never end, and saves that appeared to do nothing, with no
 * indication that anything was happening. This turns that window into one honest
 * message, and clears it by itself the moment the API answers again.
 *
 * Design rule: a failing REQUEST never puts the overlay on screen. It only starts a
 * probe of the anonymous `/api/health` liveness endpoint, and only a failing PROBE
 * does. One report timing out must not black out someone's screen.
 *
 * The probe deliberately uses bare `axios`, not `helpers/api.js`: the api instance's
 * response interceptor calls back into this module, so probing through it would
 * recurse, and its request interceptor would attach credentials the liveness probe
 * has no use for.
 */
import axios from "axios";
import { ref, readonly } from "vue";
import { classifyApiFailure, nextProbeDelay } from "./apiAvailability.mjs";

/**
 * online    — nothing wrong (no overlay)
 * checking  — a request failed; probing /health. Still NO overlay: most suspicions
 *             die here, and flashing a maintenance panel for 400ms is worse than
 *             showing nothing.
 * offline   — a probe failed. Overlay is up.
 * recovered — the API answered after an outage. Overlay switches to "back online".
 */
const _status = ref("online");
/** "server" (API unreachable) or "network" (this device has no connection). */
const _reason = ref("server");
/** Epoch ms the outage started, or null. Drives the elapsed counter in the overlay. */
const _since = ref(null);

export const apiStatus = readonly(_status);
export const apiOutageReason = readonly(_reason);
export const apiOutageSince = readonly(_since);

// How long after the first suspicion the confirming probe runs. Long enough that a
// burst of parallel failures collapses into one probe, short enough to feel instant.
const FIRST_PROBE_DELAY_MS = 400;
// The probe must give up well inside the shortest backoff step, or probes overlap.
const PROBE_TIMEOUT_MS = 6000;
// How long the "back online" panel stays before it fades itself out.
const RECOVERED_LINGER_MS = 8000;

let timer = null;
let recoveredTimer = null;
let attempt = 0;

function healthUrl() {
  const base = process.env.VUE_APP_API_BASE_URL || "http://localhost:5293/api";
  return `${base.replace(/\/+$/, "")}/health`;
}

/**
 * Kept as a seam so this file stays line-for-line comparable with the admin SPA's copy,
 * which suppresses the monitor inside the server-side print renderer. This app has no
 * such route, so nothing is suppressed here.
 */
function suppressed() {
  return false;
}

function clearTimers() {
  if (timer) { clearTimeout(timer); timer = null; }
}

function scheduleProbe(delayMs) {
  clearTimers();
  timer = setTimeout(runProbe, delayMs);
}

async function runProbe() {
  timer = null;

  // A device with its radio off will fail the probe too, but the honest message is
  // "you are offline", not "the server is being updated".
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    markOffline("network");
    scheduleProbe(nextProbeDelay(attempt++));
    return;
  }

  try {
    await axios.get(healthUrl(), {
      timeout: PROBE_TIMEOUT_MS,
      // Cache-bust: a proxy that cached one 200 would make the probe lie forever.
      params: { _t: Date.now() },
      headers: { "Cache-Control": "no-cache" },
    });
    markOnline();
  } catch {
    markOffline("server");
    scheduleProbe(nextProbeDelay(attempt++));
  }
}

function markOffline(reason) {
  _reason.value = reason;
  if (_status.value !== "offline") {
    _status.value = "offline";
    _since.value = Date.now();
  }
}

function markOnline() {
  clearTimers();
  attempt = 0;
  // The "back online" panel owns its own dismissal. Without this guard the very next
  // successful response — and after an outage there is one within milliseconds, from the
  // views that retry — would drop the panel before anyone could read it or press Reload.
  if (_status.value === "recovered") return;
  if (_status.value === "offline") {
    _status.value = "recovered";
    // Views that gave up mid-outage can listen for this and refetch.
    if (typeof window !== "undefined") window.dispatchEvent(new Event("api:online"));
    if (recoveredTimer) clearTimeout(recoveredTimer);
    recoveredTimer = setTimeout(dismissRecovered, RECOVERED_LINGER_MS);
  } else {
    _status.value = "online";
    _since.value = null;
  }
}

/** Called by the axios ERROR interceptor for every failed request. */
export function notifyApiFailure(error) {
  if (suppressed()) return;
  if (classifyApiFailure(error) !== "suspect") return;
  // Already offline or already probing — never restart the ladder, or a page full of
  // failing widgets would keep the backoff pinned at its shortest step.
  if (_status.value === "offline" || _status.value === "checking") return;
  _status.value = "checking";
  attempt = 0;
  scheduleProbe(FIRST_PROBE_DELAY_MS);
}

/** Called by the axios SUCCESS interceptor. Any 2xx proves the API is up. */
export function notifyApiSuccess() {
  if (_status.value === "online") return; // hot path — one comparison per response
  markOnline();
}

/** "Retry now" in the overlay: probe immediately instead of waiting out the backoff. */
export function retryNow() {
  if (_status.value !== "offline") return;
  scheduleProbe(0);
}

/** Dismiss the "back online" panel. */
export function dismissRecovered() {
  if (recoveredTimer) { clearTimeout(recoveredTimer); recoveredTimer = null; }
  if (_status.value === "recovered") {
    _status.value = "online";
    _since.value = null;
  }
}

/** Reload the SPA — after a deploy the served bundle has new chunk hashes. */
export function reloadApp() {
  if (typeof window !== "undefined") window.location.reload();
}

// A device coming back onto a network is a free, instant signal — don't sit out the
// remaining backoff waiting to rediscover it.
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    if (_status.value === "offline") scheduleProbe(0);
  });
}
